/**
 * Decorator-free enums + interval interface for the procedural timeline.
 *
 * Lives outside the entity file so the frontend can import these as runtime
 * values without Vite/SWC trying to parse the entity's TypeORM decorators —
 * see the `procedural-timeline-limits.ts` pattern for the same workaround on
 * the upload side.
 *
 * The entity re-exports these so backend callers can keep their existing
 * `from './procedural-timeline-event.entity.js'` imports unchanged.
 */

/**
 * Legal jurisdiction the event was extracted under. Chosen by the user at
 * upload and forwarded to the extractor, which uses it to resolve
 * jurisdiction-dependent day-count rules (working / clear / court days).
 */
export enum ProceduralJurisdiction {
  EnglandAndWales = 'England & Wales',
  Other = 'Other',
  USFederal = 'US Federal',
}

/** How a `deltaInterval` is counted off the anchor event's date. */
export enum DeltaIntervalType {
  Calendar = 'calendar',
  Working = 'working',
  Clear = 'clear',
  Court = 'court',
}

/**
 * Provenance / trust signal on the event.
 *
 *  - `Low` / `Medium` / `High` — set by the extractor based on how unambiguous
 *    the source wording was. Any non-calendar `deltaIntervalType` defaults to
 *    `Low` or `Medium` because the day-count rule depends on jurisdiction.
 *  - `Manual` — the user has created or edited this event. Wexler stamps.
 */
export enum EventConfidence {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Manual = 'manual',
}

/**
 * PostgreSQL `interval` value. Months and years are preserved as their own
 * components (not converted to days) so deltas like `'-1.5 months'` round-trip
 * without lossy conversion.
 */
export interface DeltaInterval {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}
